import { Asset } from 'base/asset';
import { Avatar } from 'base/ui/avatar';
import { Tooltip } from 'base/ui/tooltip';
import { TextSmall } from 'base/ui/typography';
import { manageAccountRouteToUrl } from 'blocks/manage_account/routes';
import classNames from 'classnames';
import React, { useMemo } from 'react';
import styles from './avatar_group.css';

type Background = 'white' | 'grey';

interface User {
  firstName: string;
  lastName: string;
  avatar?: Asset;
}

interface MaybeUser {
  id: number;
  color: string;
  self: boolean;
  user?: User;
}

interface Props {
  avatars: readonly MaybeUser[];
  bordered?: boolean;
  background: Background;
  maxVisible: number;
  overlap?: boolean;
  size?: 'small' | 'medium' | 'large';
}

export function AvatarGroup({
  avatars,
  bordered,
  background,
  maxVisible,
  overlap,
  size = 'medium',
}: Props) {
  const { visible, overflow } = useMemo(() => {
    // Always show self first
    const visible = [
      ...avatars.filter((s) => s.self),
      ...avatars.filter((s) => !s.self),
    ];

    const overflow =
      visible.length > maxVisible ? visible.splice(maxVisible) : [];

    visible.reverse();

    return { visible, overflow };
  }, [maxVisible, avatars]);

  return (
    <div className={classNames(styles.container, overlap && styles.overlap)}>
      {overflow.length > 0 && (
        <Overflow background={background} users={overflow} size={size} />
      )}
      {visible.map((user) => (
        <AvatarWithTooltip
          key={user.id}
          self={user.self}
          background={background}
          bordered={bordered}
          color={user.color}
          user={user.user}
          size={size}
        />
      ))}
    </div>
  );
}

function Overflow({
  users,
  background,
  size,
}: {
  users: readonly MaybeUser[];
  background: Background;
  size: 'small' | 'medium' | 'large';
}) {
  const n = Math.min(users.length, 9);

  return (
    <a
      href={manageAccountRouteToUrl({
        path: 'manage-account/team',
      })}
      className={classNames(
        styles.avatar,
        styles.isOverflow,
        styles[background],
        styles[size],
      )}
    >
      <TextSmall>+{n}</TextSmall>
    </a>
  );
}

function AvatarWithTooltip({
  color,
  self,
  background,
  bordered,
  user,
  size,
}: {
  color: string;
  self: boolean;
  background: Background;
  bordered?: boolean;
  size: 'small' | 'medium' | 'large';
  user?: User;
}) {
  const ref = React.useRef<HTMLElement>(null);

  let tooltipText = user
    ? `${user.firstName} ${user.lastName}`.trim()
    : 'Anonymous';
  if (self) {
    tooltipText += ' (you)';
  }

  return (
    <div
      className={classNames(styles.avatar, styles[background], styles[size])}
    >
      <Avatar
        ref={ref}
        color={color}
        bordered={bordered}
        size={size}
        isAnonymous={!user}
        {...user}
      />
      <Tooltip anchorRef={ref} label={tooltipText} placement="vertical" />
    </div>
  );
}
